import { useApolloClient } from "@apollo/client";
import { assert, getDataOrNull } from "@msys/common";
import {
  CardItem,
  CollapseSection,
  DataGrid,
  GridColDef,
  GridRowId,
  GridRowParams,
  GridTreeDataGroupingCell,
  ListHeader,
  LoadingSpinner,
  MenuButton,
  MenuItemWithIcon,
  ModalOpenButton,
  PriceInput,
  useScreenWidth,
} from "@msys/ui";
import {
  ArrowDropDown as ArrowDropDownIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowRight as ArrowRightIcon,
  Delete as DeleteIcon,
  InsertDriveFile as InsertDriveFileIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  TextField,
  TextFieldProps,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Form, Formik, useFormikContext } from "formik";
import { difference, intersection, isEqual, isString, values } from "lodash-es";
import { useSnackbar } from "notistack";
import React from "react";
import {
  Link,
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import * as Yup from "yup";
import {
  BillOfMaterialsItemType,
  namedOperations,
} from "../../../clients/graphqlTypes.js";
import { AutoSave } from "../../commons/form-fields/AutoSave.js";
import {
  BreadcrumbItem,
  Page,
  PageTopbarItem,
} from "../../commons/layout/Page.js";
import { PageContainer } from "../../commons/layout/PageContainer.js";
import { PageHeader } from "../../commons/layout/PageHeader.js";
import { Stack } from "../../commons/layout/Stack.js";
import { ConfirmModal } from "../../commons/modals/ConfirmModal.js";
import { NoPropagateBox } from "../../commons/NoPropagateBox.js";
import { PageNotFound } from "../../commons/PageNotFound.js";
import { browserHasOnlyTouch } from "../../featureDetection.js";
import { BillOfMaterialsListItemEdit } from "../../features/bill-of-materials/BillOfMaterialListItem.js";
import { BillOfMaterialsItemFragment } from "../../features/bill-of-materials/BillOfMaterials.generated.js";
import { BillOfMaterialsItemQuantity } from "../../features/bill-of-materials/BillOfMaterialsItemQuantity.js";
import { BillOfMaterialsItemStatusLabel } from "../../features/bill-of-materials/BillOfMaterialsItemStatus.js";
import {
  QuickFilterValue,
  useBillOfMaterialsFilters,
} from "../../features/bill-of-materials/useBillOfMaterialsFilters.js";
import { useItemGroups } from "../../features/bill-of-materials/useItemGroups.js";
import { useQuantityUnits } from "../../features/doc-items/useQuantityUnits.js";
import {
  MaterialItemTotalPrice,
  OrderDetailsBox,
} from "../../features/purchase-orders/boxes/OrderDetailsBox.js";
import { PurchaseOrderItemDetails } from "../../features/purchase-orders/PurchaseOrderItemDetails.js";
import { SelectSupplierAndDeliveryModal } from "../../features/purchase-orders/PurchaseOrderSelectSupplierAndDeliveryModal.js";
import { PurchaseOrderTitle } from "../../features/purchase-orders/PurchaseOrderTitle.js";
import { useDataGridStateStore } from "../../features/users/useDataGridStateStore.js";
import { useProjectBillOfMaterialsQuery } from "./ProjectBillOfMaterials.generated.js";
import {
  PurchaseOrderEdit_OrderItemFragment,
  useAddPurchaseOrderItemsMutation,
  useDeletePurchaseOrderMutation,
  useModifyPurchaseOrderItemMutation,
  useModifyPurchaseOrderMutation,
  usePurchaseOrderEditItemQuery,
  usePurchaseOrderEditQuery,
  useRemovePurchaseOrderItemsMutation,
} from "./ProjectPurchaseOrderEdit.generated.js";

interface FormValues {
  [key: string]: {
    quantityOrdered: number;
    unitPrice: number;
  };
}

interface Props {
  projectId: string;
  prefixBreadcrumbs: BreadcrumbItem[];
  submenuItems: PageTopbarItem[];
  activeSubmenuItem: PageTopbarItem | undefined;
}

export function ProjectPurchaseOrderEdit({
  projectId,
  prefixBreadcrumbs,
  submenuItems,
  activeSubmenuItem,
}: Props) {
  const { orderId } = useParams();
  if (!orderId) throw new Error("Order id is missing");
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const location = useLocation();

  const { t } = useTranslate(["Materials", "PurchaseOrders", "Global"]);
  const { isMaxPhone } = useScreenWidth();

  const { groups } = useItemGroups();
  const {
    filters,
    activeQuickFilter,
    quickFilters,
    OpenFiltersButton,
    FilterChips,
    Search,
    QuickFilters,
  } = useBillOfMaterialsFilters(projectId);

  const client = useApolloClient();
  const bomQuery = useProjectBillOfMaterialsQuery({
    client,
    variables: {
      projectId,
      ...quickFilters,
      ...filters,
    },
    fetchPolicy: "cache-and-network",
  });
  const orderQuery = usePurchaseOrderEditQuery({
    client,
    variables: {
      projectId,
      orderId,
    },
    fetchPolicy: "cache-and-network",
  });

  const [modifyPurchaseOrder] = useModifyPurchaseOrderMutation({
    client,
    ignoreResults: true,
  });
  const [deletePurchaseOrder] = useDeletePurchaseOrderMutation({
    client,
    ignoreResults: true,
    variables: { orderId },
  });

  const project = getDataOrNull(orderQuery.data?.project)?.project;
  const order = project?.order;

  const breadcrumbs = React.useMemo(
    () => [
      ...prefixBreadcrumbs,
      {
        title: t("Purchase Orders", {
          ns: "PurchaseOrders",
        }),
        to: `/projects/${projectId}/materials/purchase-orders`,
      },
      {
        title: order?.number ?? "",
        to: `/projects/${projectId}/materials/purchase-orders/${orderId}/edit`,
      },
    ],
    [prefixBreadcrumbs, t, projectId, order?.number, orderId]
  );

  const handleRowClick = React.useCallback(
    (item: BillOfMaterialsItemFragment) => {
      navigate({
        pathname: `/projects/${projectId}/materials/purchase-orders/${orderId}/edit/items/${item.id}`,
        search: location.search,
      });
    },
    [location.search, navigate, orderId, projectId]
  );

  const orderQueryIsLoading = orderQuery.loading && !orderQuery.data;
  const bomQueryIsLoading = bomQuery.loading && !bomQuery.data;
  const isLoading = orderQueryIsLoading || bomQueryIsLoading;

  const materialItems = React.useMemo(
    () => getDataOrNull(bomQuery.data?.project)?.project?.billOfMaterialsItems,
    [bomQuery.data?.project]
  );
  const purchaseOrderItems = React.useMemo(() => order?.items, [order?.items]);

  const handleSubmit = useSubmitOrderItems(
    orderId,
    materialItems ?? [],
    purchaseOrderItems ?? []
  );

  const initialValues: FormValues = React.useMemo(() => {
    if (!purchaseOrderItems || !materialItems) return {};
    return purchaseOrderItems.reduce((acc, orderItem) => {
      const matchingBomItem = materialItems?.find(bomItem =>
        isBomItemEqualToOrderItem(bomItem, orderItem)
      );
      if (!matchingBomItem) return acc;
      acc[matchingBomItem.id] = {
        quantityOrdered: orderItem.quantityOrdered,
        unitPrice: orderItem.unitPrice,
      };
      return acc;
    }, {} as FormValues);
  }, [materialItems, purchaseOrderItems]);

  const validationSchema = React.useMemo(
    () =>
      Yup.array()
        .transform((_a, obj) => values(obj))
        .of(
          Yup.object().shape({
            quantityOrdered: Yup.number()
              .label(t("Order quantity", { ns: "PurchaseOrders" }))
              .min(0),
            unitPrice: Yup.number()
              .label(t("Order price/unit", { ns: "PurchaseOrders" }))
              .min(0),
          })
        ),
    [t]
  );

  if (order?.orderedDate)
    // no edit view for already ordered purchase order
    return (
      <Navigate
        to={`/projects/${projectId}/materials/purchase-orders/${orderId}`}
        replace
      />
    );

  return (
    <Page
      subtitle={project?.title}
      title={t("Bill of materials", {
        ns: "Materials",
      })}
      submenuItems={submenuItems}
      breadcrumbs={breadcrumbs}
      header={
        <PageHeader
          breadcrumbs={breadcrumbs}
          submenuItems={submenuItems}
          activeSubmenuItem={activeSubmenuItem}
        />
      }
      aside={<Outlet />}
      action={
        order?.supplier?.id ? (
          <Button
            variant="outlined"
            endIcon={<InsertDriveFileIcon />}
            component={Link}
            to={`/projects/${projectId}/materials/purchase-orders/${orderId}`}
          >
            {t("Preview", {
              ns: "PurchaseOrders",
            })}
          </Button>
        ) : (
          <ModalOpenButton
            Modal={SelectSupplierAndDeliveryModal}
            modalProps={{
              projectId,
              handleComplete: async (crmSupplierId, address, addressType) => {
                await modifyPurchaseOrder({
                  variables: {
                    input: {
                      orderId,
                      crmSupplierId,
                      deliveryAddress: address,
                      deliveryAddressType: addressType,
                    },
                  },
                });
                navigate(
                  `/projects/${projectId}/materials/purchase-orders/${orderId}`
                );
              },
            }}
          >
            <Button
              variant="contained"
              color="primary"
              endIcon={<ArrowForwardIcon />}
            >
              {t("Next", {
                ns: "Global",
              })}
            </Button>
          </ModalOpenButton>
        )
      }
    >
      <PageContainer $fullHeight>
        {isLoading ? (
          <LoadingSpinner />
        ) : !order ? (
          <PageNotFound />
        ) : (
          <Formik<FormValues>
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={values => handleSubmit(values, initialValues)}
          >
            <Form>
              <ListHeader
                marginBottom={2}
                title={<PurchaseOrderTitle order={order} />}
                hideTitleOnMobile
                MenuButton={
                  <MenuButton>
                    <ModalOpenButton
                      Modal={ConfirmModal}
                      modalProps={{
                        handleConfirm: async () => {
                          await deletePurchaseOrder();
                          navigate(
                            `/projects/${projectId}/materials/purchase-orders`
                          );
                          enqueueSnackbar(
                            t("Purchase order deleted", {
                              ns: "PurchaseOrders",
                            })
                          );
                        },
                      }}
                    >
                      <MenuItemWithIcon icon={<DeleteIcon />}>
                        {t("Delete", {
                          ns: "Global",
                        })}
                      </MenuItemWithIcon>
                    </ModalOpenButton>
                  </MenuButton>
                }
                QuickFilter={
                  <QuickFilters activeQuickFilter={activeQuickFilter} />
                }
                FilterButton={OpenFiltersButton}
                SearchField={Search}
                FilterChips={FilterChips}
              />
              {isMaxPhone ? (
                <Stack flexDirection="column">
                  {groups.map(group => (
                    <CollapseSection key={group.id} title={group.label}>
                      <Stack flexDirection="column">
                        {materialItems
                          ?.filter(item => item.itemType === group.id)
                          .map(item => (
                            <CardItem
                              key={item.id}
                              onClick={() => {
                                handleRowClick(item);
                              }}
                            >
                              <BillOfMaterialsListItemEdit
                                item={item}
                                Checkbox={<OrderQuantityCheckbox item={item} />}
                                QuantityInput={
                                  <OrderQuantityInput
                                    item={item}
                                    label={t("Order qty", {
                                      ns: "PurchaseOrders",
                                    })}
                                  />
                                }
                              />
                            </CardItem>
                          ))}
                      </Stack>
                    </CollapseSection>
                  ))}
                </Stack>
              ) : (
                <PurchaseOrderEditTable
                  items={materialItems ?? []}
                  handleRowClick={handleRowClick}
                  activeQuickFilter={activeQuickFilter}
                  isLoading={isLoading}
                />
              )}
              <AutoSave enableReinitialize initialValues={initialValues} />
            </Form>
          </Formik>
        )}
      </PageContainer>
    </Page>
  );
}

function OrderQuantityCheckbox({
  item,
}: {
  item: BillOfMaterialsItemFragment;
}) {
  const { values, setValues } = useFormikContext<FormValues>();
  return (
    <Checkbox
      checked={Boolean(values[item.id]?.quantityOrdered)}
      onChange={async (_, checked) => {
        if (checked) {
          await setValues({
            ...values,
            [item.id]: {
              quantityOrdered: Math.max(
                item.quantityRequiredTotal - item.quantityOrdered,
                1
              ),
              unitPrice: item.netPrice ?? item.listPrice ?? 0,
            },
          });
        } else {
          await setValues(
            Object.fromEntries(
              Object.entries(values).filter(([k, v]) => k !== item.id)
            )
          );
        }
      }}
      onClick={event => event.stopPropagation()}
    />
  );
}

function OrderQuantityInput({
  item,
  label,
  size,
}: {
  item: BillOfMaterialsItemFragment;
  label?: string;
  size?: TextFieldProps["size"];
}) {
  const { values, setValues, errors, touched } = useFormikContext<FormValues>();
  const error = errors?.[item.id]?.quantityOrdered;
  const touch = touched?.[item.id]?.quantityOrdered;
  const value = values[item.id]?.quantityOrdered;
  return (
    <TextField
      size={size}
      type="number"
      label={label}
      defaultValue={0}
      value={value}
      fullWidth={false}
      // onFocus={event => {
      //   setTimeout(() => event.target.select()); // select input value when focused
      // }}
      onSelect={
        browserHasOnlyTouch
          ? event => {
              event.preventDefault(); // prevent context menu from opening (on mobile) when selecting text
            }
          : undefined
      }
      onClick={event => event.stopPropagation()}
      onChange={async event => {
        const value = Number(event.target.value);
        if (!event.target.value || value <= 0) {
          if (values[item.id]) {
            await setValues(
              Object.fromEntries(
                Object.entries(values).filter(([k, v]) => k !== item.id)
              )
            );
          }
          return;
        }
        if (value && value > 0 && !values[item.id]) {
          await setValues({
            ...values,
            [item.id]: {
              quantityOrdered: value,
              unitPrice: item.netPrice ?? item.listPrice ?? 0,
            },
          });
          return;
        }
        if (value && value > 0 && values[item.id]) {
          await setValues({
            ...values,
            [item.id]: {
              ...values[item.id],
              quantityOrdered: value,
            },
          });
          return;
        }
      }}
      slotProps={{
        input: { fullWidth: false },
        htmlInput: { min: 0 },
      }}
      error={touch && isString(error) ? true : undefined}
    />
  );
}

function OrderUnitPriceInput({
  item,
  label,
  size,
}: {
  item: BillOfMaterialsItemFragment;
  label?: string;
  size?: TextFieldProps["size"];
}) {
  const { values, setValues, errors, touched } = useFormikContext<FormValues>();
  const error = errors?.[item.id]?.unitPrice;
  const touch = touched?.[item.id]?.unitPrice;
  const value = values[item.id]?.unitPrice;
  const valueQty = values[item.id]?.quantityOrdered;
  return (
    <PriceInput
      size={size}
      label={label}
      fullWidth={false}
      disabled={!valueQty}
      value={value ?? item.netPrice ?? item.listPrice ?? 0}
      min={0}
      onChange={async value => {
        if (values[item.id]) {
          await setValues({
            ...values,
            [item.id]: {
              ...values[item.id],
              unitPrice: value,
            },
          });
        }
      }}
      // onFocus={event => {
      //   setTimeout(() => event.target.select()); // select input value when focused
      // }}
      onSelect={
        browserHasOnlyTouch
          ? event => {
              event.preventDefault(); // prevent context menu from opening (on mobile) when selecting text
            }
          : undefined
      }
      onClick={event => event.stopPropagation()}
      error={touch && isString(error) ? true : undefined}
    />
  );
}

function PurchaseOrderEditTable({
  items,
  handleRowClick,
  activeQuickFilter,
  isLoading,
}: {
  items: BillOfMaterialsItemFragment[];
  handleRowClick: (item: BillOfMaterialsItemFragment) => void;
  activeQuickFilter: QuickFilterValue | undefined;
  isLoading: boolean | undefined;
}) {
  const { t } = useTranslate(["Materials", "PurchaseOrders", "Global"]);
  const { groupLabels } = useItemGroups();
  const { quantityUnitLabels } = useQuantityUnits();
  const { values, setValues } = useFormikContext<FormValues>();

  const columns: GridColDef<BillOfMaterialsItemFragment>[] = React.useMemo(
    () => [
      {
        field: "itemStatus",
        headerName: t("Status", {
          ns: "Materials",
        }),
        sortable: false,
        groupable: false,
        flex: 1,
        minWidth: 100,
        renderCell: ({ row, rowNode }) =>
          rowNode.type !== "group" && (
            <BillOfMaterialsItemStatusLabel status={row.itemStatus} />
          ),
      },
      {
        field: "supplier",
        headerName: t("Supplier", {
          ns: "Materials",
        }),
        sortable: false,
        groupable: false,
        flex: 2,
        minWidth: 200,
        renderCell: ({ row, rowNode }) =>
          rowNode.type !== "group" && row.productSupplier?.title,
      },
      {
        field: "productArticleNumber",
        headerName: t("Article #", {
          ns: "Materials",
        }),
        sortable: false,
        groupable: false,
        flex: 1.5,
        minWidth: 150,
        renderCell: ({ row, rowNode }) =>
          rowNode.type !== "group" && row.productArticleNumber,
      },
      {
        field: "productManufacturerArticleNumber",
        headerName: t("Manufacturer article #", {
          ns: "Materials",
        }),
        sortable: false,
        groupable: false,
        flex: 1.5,
        minWidth: 150,
        renderCell: ({ row, rowNode }) =>
          rowNode.type !== "group" && row.productManufacturerArticleNumber,
      },
      {
        field: "quantityUnit",
        headerName: t("Unit", {
          ns: "Materials",
        }),
        sortable: false,
        groupable: false,
        flex: 1,
        minWidth: 100,
        renderCell: ({
          row,
          rowNode,
        }: {
          row: BillOfMaterialsItemFragment;
          rowNode: any;
        }) => rowNode.type !== "group" && quantityUnitLabels[row.quantityUnit],
      },
      {
        field: "quantityRequiredTotal",
        headerName: t("Required", {
          ns: "Materials",
        }),
        headerAlign: "right",
        align: "right",
        sortable: false,
        groupable: false,
        flex: 1,
        minWidth: 100,
        renderCell: ({ row, rowNode }) =>
          rowNode.type !== "group" && (
            <BillOfMaterialsItemQuantity
              item={row}
              showAlerts={activeQuickFilter === undefined}
            />
          ),
      },
      {
        field: "quantityOrdered",
        headerName: t("Already ordered", {
          ns: "Materials",
        }),
        headerAlign: "right",
        align: "right",
        sortable: false,
        groupable: false,
        flex: 1,
        minWidth: 100,
        renderCell: ({ row, rowNode }) =>
          rowNode.type !== "group" && row.quantityOrdered,
      },
      {
        field: "orderQuantity",
        headerName: t("Order quantity", {
          ns: "PurchaseOrders",
        }),
        headerAlign: "right",
        align: "right",
        sortable: false,
        groupable: false,
        flex: 1,
        minWidth: 100,
        renderCell: ({ row, rowNode }) =>
          rowNode.type !== "group" && (
            <NoPropagateBox>
              <OrderQuantityInput item={row} size="extra-small" />
            </NoPropagateBox>
          ),
      },
      {
        field: "orderPrice",
        headerName: t("Order price/unit", {
          ns: "PurchaseOrders",
        }),
        headerAlign: "right",
        align: "right",
        sortable: false,
        groupable: false,
        flex: 1,
        minWidth: 100,
        renderCell: ({ row, rowNode }) =>
          rowNode.type !== "group" && (
            <NoPropagateBox>
              <OrderUnitPriceInput item={row} size="extra-small" />
            </NoPropagateBox>
          ),
      },
      {
        field: "totalPrice",
        headerName: t("Total price", {
          ns: "PurchaseOrders",
        }),
        headerAlign: "right",
        align: "right",
        sortable: false,
        groupable: false,
        flex: 1,
        minWidth: 100,
        renderCell: ({ row, rowNode }) => {
          if (rowNode.type === "group") return null;
          return <MaterialItemTotalPrice item={row} />;
        },
      },
    ],
    [t, quantityUnitLabels, activeQuickFilter]
  );

  const groupingColDef = React.useMemo(
    () =>
      ({
        headerName: t("Product title", {
          ns: "Materials",
        }),
        // leafField: "title",
        // sortable: true,
        // sortingOrder: ["asc", "desc"],
        // sortComparator: (value1, value2, cellParams1, cellParams2) => {
        //   if (
        //     cellParams1.rowNode.isAutoGenerated ||
        //     cellParams2.rowNode.isAutoGenerated
        //   ) {
        //     return (
        //       groupRanks[value1 as BillOfMaterialsItemType] -
        //       groupRanks[value2 as BillOfMaterialsItemType]
        //     );
        //   }

        //   return 0;
        // },
        flex: 3,
        minWidth: 200,
        renderCell: params => {
          if (params.rowNode.type === "group") {
            return (
              <GridTreeDataGroupingCell
                {...params}
                rowNode={{
                  ...params.rowNode,
                  groupingKey:
                    groupLabels[
                      params.rowNode.groupingKey as BillOfMaterialsItemType
                    ],
                }}
                formattedValue={
                  groupLabels[
                    params.rowNode.groupingKey as BillOfMaterialsItemType
                  ]
                }
              />
            );
          }

          return <Box marginLeft={"44px"}>{params.row.title}</Box>;
        },
      }) as GridColDef<BillOfMaterialsItemFragment>,
    [groupLabels, t]
  );

  const selectedItemIds = React.useMemo(() => Object.keys(values), [values]);
  const stateStore = useDataGridStateStore("ProjectPurchaseOrderEdit");

  return (
    <DataGrid
      stateStore={stateStore}
      columns={columns}
      rows={items}
      sortingMode={"client"}
      paginationMode={"client"}
      // sortModel={[
      //   { field: "itemType", sort: "asc" },
      //   { field: "title", sort: "asc" },
      // ]}
      loading={isLoading}
      hideFooter
      // onRowClick={params => {
      //   if (params.row.id) {
      //     handleRowClick(params.row as BillOfMaterialsItemFragment);
      //   }
      // }}
      onCellClick={(params, event) => {
        event.defaultMuiPrevented = true;
        if (
          params.row.id &&
          [
            "__tree_data_group__",
            "itemStatus",
            "supplier",
            "productArticleNumber",
            "productManufacturerArticleNumber",
          ].includes(params.field)
        )
          handleRowClick(params.row as BillOfMaterialsItemFragment);
      }}
      checkboxSelection
      disableRowSelectionOnClick
      rowSelectionModel={selectedItemIds}
      isRowSelectable={(params: GridRowParams) => {
        return !!params.row.id;
      }}
      onRowSelectionModelChange={async newSelectionModel => {
        const isGroupRow = (id: GridRowId) =>
          (id as string).startsWith("auto-generated-row");

        const toAdd = difference(newSelectionModel, selectedItemIds).filter(
          id => !isGroupRow(id)
        );
        const toRemove = difference(selectedItemIds, newSelectionModel).filter(
          id => !isGroupRow(id)
        );

        let newValues = { ...values };

        if (toAdd.length > 0) {
          newValues = {
            ...newValues,
            ...toAdd.reduce((acc, id) => {
              const item = items.find(item => item.id === id);
              if (!item) return acc;
              acc[id] = {
                quantityOrdered: Math.max(
                  item.quantityRequiredTotal - item.quantityOrdered,
                  1
                ),
                unitPrice: item.netPrice ?? item.listPrice ?? 0,
              };
              return acc;
            }, {} as FormValues),
          };
        }
        if (toRemove.length > 0) {
          newValues = Object.fromEntries(
            Object.entries(newValues).filter(([k, v]) => !toRemove.includes(k))
          );
        }

        await setValues(newValues);
      }}
      treeData
      getTreeDataPath={row => [row.itemType, row.id]}
      // disableRowGrouping
      // experimentalFeatures={{ rowGrouping: true }}
      // rowGroupingColumnMode="single"
      groupingColDef={groupingColDef}
      isGroupExpandedByDefault={() => true}
      // initialState={{
      //   rowGrouping: {
      //     model: ["title"],
      //   },
      // }}
      slots={{
        treeDataCollapseIcon: ArrowDropDownIcon,
        treeDataExpandIcon: ArrowRightIcon,
      }}
    />
  );
}

// const groupRanks: Record<BillOfMaterialsItemType, number> = {
//   [BillOfMaterialsItemType.Product]: 0,
//   [BillOfMaterialsItemType.CustomProduct]: 1,
//   [BillOfMaterialsItemType.QuoteItem]: 2,
// };

export function ProjectPurchaseOrderEditItem({
  projectId,
}: {
  projectId: string;
}) {
  const { orderId, itemId } = useParams();
  if (!orderId) throw new Error("Order id is missing");
  if (!itemId) throw new Error("Material item id is missing");
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { t } = useTranslate(["Materials", "PurchaseOrders", "Global"]);

  const client = useApolloClient();
  const query = usePurchaseOrderEditItemQuery({
    client,
    variables: {
      projectId,
      orderId,
    },
  });
  const bomQuery = useProjectBillOfMaterialsQuery({
    client,
    variables: {
      projectId,
      aggregateProducts: true,
    },
    fetchPolicy: "cache-and-network",
  });

  const order = getDataOrNull(query.data?.project)?.project?.order;

  const orderQueryIsLoading = query.loading && !query.data;
  const bomQueryIsLoading = bomQuery.loading && !bomQuery.data;
  const isLoading = orderQueryIsLoading || bomQueryIsLoading;

  const materialItems = React.useMemo(
    () => getDataOrNull(bomQuery.data?.project)?.project?.billOfMaterialsItems,
    [bomQuery.data?.project]
  );
  const purchaseOrderItems = React.useMemo(() => order?.items, [order?.items]);

  const handleSubmit = useSubmitOrderItems(
    orderId,
    materialItems ?? [],
    purchaseOrderItems ?? []
  );

  const initialValues: FormValues = React.useMemo(() => {
    if (!purchaseOrderItems || !materialItems) return {};
    return purchaseOrderItems.reduce((acc, orderItem) => {
      const matchingBomItem = materialItems?.find(bomItem =>
        isBomItemEqualToOrderItem(bomItem, orderItem)
      );
      if (!matchingBomItem) return acc;
      acc[matchingBomItem.id] = {
        quantityOrdered: orderItem.quantityOrdered,
        unitPrice: orderItem.unitPrice,
      };
      return acc;
    }, {} as FormValues);
  }, [materialItems, purchaseOrderItems]);

  const validationSchema = React.useMemo(
    () =>
      Yup.array()
        .transform((_a, obj) => values(obj))
        .of(
          Yup.object().shape({
            quantityOrdered: Yup.number()
              .label(t("Order quantity", { ns: "PurchaseOrders" }))
              .min(0),
            unitPrice: Yup.number()
              .label(t("Order price/unit", { ns: "PurchaseOrders" }))
              .min(0),
          })
        ),
    [t]
  );

  const item = materialItems?.find(item => item.id === itemId);

  if (isLoading) return <LoadingSpinner />;
  if (!item) return <PageNotFound />;

  return (
    <Formik<FormValues>
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={values => handleSubmit(values, initialValues)}
    >
      <Form>
        <PurchaseOrderItemDetails
          projectId={projectId}
          OrderDetailBox={
            item ? (
              <OrderDetailsBox
                materialItem={item}
                QuantityInput={
                  <OrderQuantityInput item={item} size="extra-small" />
                }
                PriceInput={
                  <OrderUnitPriceInput item={item} size="extra-small" />
                }
              />
            ) : null
          }
          handleClose={() =>
            navigate({
              pathname: `/projects/${projectId}/materials/purchase-orders/${orderId}/edit`,
              search: searchParams.toString(),
            })
          }
        />
        <AutoSave enableReinitialize initialValues={initialValues} />
      </Form>
    </Formik>
  );
}

function useAddOrderItems(orderId: string) {
  const client = useApolloClient();
  const [addPurchaseOrderItems] = useAddPurchaseOrderItemsMutation({
    client,
  });

  const addOrderItems = React.useCallback(
    async (
      items: {
        item: BillOfMaterialsItemFragment;
        quantityOrdered?: number;
        unitPrice?: number;
      }[]
    ) => {
      await addPurchaseOrderItems({
        variables: {
          input: {
            orderId,
            items: items.map(({ item, quantityOrdered, unitPrice }) => ({
              originProductArticleNumber: item.productArticleNumber,
              originProductSupplierId: item.productSupplier?.id,
              originQuoteItemId: item.quoteItemId,
              productArticleNumber: item.productArticleNumber,
              productManufacturerArticleNumber:
                item.productManufacturerArticleNumber,
              productTitle: item.title,
              quantity:
                quantityOrdered ??
                Math.max(item.quantityRequiredTotal - item.quantityOrdered, 1),
              unitPrice: unitPrice ?? item.netPrice ?? item.listPrice ?? 0,
              quantityUnit: item.quantityUnit,
            })),
          },
        },
      });
    },
    [addPurchaseOrderItems, orderId]
  );

  return addOrderItems;
}

function useModifyOrderItem(orderId: string) {
  const client = useApolloClient();
  const [modifyPurchaseOrderItem] = useModifyPurchaseOrderItemMutation({
    client,
  });

  const modifyOrderItem = React.useCallback(
    async (
      itemId: string,
      {
        quantityOrdered,
        unitPrice,
      }: {
        quantityOrdered?: number;
        unitPrice?: number;
      }
    ) => {
      await modifyPurchaseOrderItem({
        variables: {
          input: {
            orderId,
            itemId,
            quantity: quantityOrdered,
            unitPrice,
          },
        },
      });
    },
    [modifyPurchaseOrderItem, orderId]
  );

  return modifyOrderItem;
}

function useRemoveOrderItems(orderId: string) {
  const client = useApolloClient();
  const [removePurchaseOrderItems] = useRemovePurchaseOrderItemsMutation({
    client,
  });

  const removeOrderItems = React.useCallback(
    async (itemIds: string[]) => {
      await removePurchaseOrderItems({
        variables: {
          input: {
            orderId,
            itemIds,
          },
        },
      });
    },
    [orderId, removePurchaseOrderItems]
  );

  return removeOrderItems;
}

function useSubmitOrderItems(
  orderId: string,
  bomItems: BillOfMaterialsItemFragment[],
  orderItems: PurchaseOrderEdit_OrderItemFragment[]
) {
  const client = useApolloClient();

  const addOrderItems = useAddOrderItems(orderId);
  const modifyOrderItem = useModifyOrderItem(orderId);
  const removeOrderItems = useRemoveOrderItems(orderId);

  const handleSubmit = async (
    values: FormValues,
    initialValues: FormValues
  ) => {
    const newItemIds = Object.keys(values);
    const existingItemIds = Object.keys(initialValues);

    const itemIdsToAdd = difference(newItemIds, existingItemIds);
    const itemIdsToRemove = difference(existingItemIds, newItemIds);
    const itemIdsToModify = intersection(newItemIds, existingItemIds).filter(
      itemId => {
        const newItem = values[itemId];
        const existingItem = initialValues[itemId];
        assert(newItem, "New item not found");
        assert(existingItem, "Existing item not found");
        return !isEqual(existingItem, newItem);
      }
    );

    if (itemIdsToAdd.length > 0) {
      await addOrderItems(
        itemIdsToAdd.map(itemId => {
          const item = bomItems.find(item => item.id === itemId);
          const value = values[itemId];
          assert(item, "BOM item not found");
          assert(value, "Value not found");
          return {
            item,
            quantityOrdered: value.quantityOrdered,
            unitPrice: value.unitPrice,
          };
        })
      );
    }
    if (itemIdsToRemove.length > 0) {
      await removeOrderItems(
        itemIdsToRemove.map(itemId => {
          const bomItem = bomItems.find(item => item.id === itemId);
          assert(bomItem, "BOM item not found");
          const orderItem = orderItems.find(orderItem =>
            isBomItemEqualToOrderItem(bomItem, orderItem)
          );
          assert(orderItem, "Order item not found");
          return orderItem.id;
        })
      );
    }
    if (itemIdsToModify.length > 0) {
      await Promise.all(
        itemIdsToModify.map(async itemId => {
          const newItem = values[itemId];
          assert(newItem, "New item not found");
          const bomItem = bomItems.find(item => item.id === itemId);
          assert(bomItem, "BOM item not found");
          const orderItem = orderItems.find(orderItem =>
            isBomItemEqualToOrderItem(bomItem, orderItem)
          );
          assert(orderItem, "Order item not found");
          await modifyOrderItem(orderItem.id, newItem);
        })
      );
    }

    await client.refetchQueries({
      include: [namedOperations.Query.PurchaseOrderEdit],
    });
  };

  return handleSubmit;
}

function isBomItemEqualToOrderItem(
  bomItem: BillOfMaterialsItemFragment,
  orderItem: PurchaseOrderEdit_OrderItemFragment
) {
  return (
    (bomItem.productSupplier?.id ?? null) ===
      orderItem.originProductSupplierId &&
    orderItem.originProductArticleNumber === bomItem.productArticleNumber &&
    orderItem.originQuoteItemId === bomItem.quoteItemId
  );
}
